import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";

import { auth, db } from "../../firebase/firebase";
import { sendMessageToPersonalTrainer } from "../../services/personalTrainerClient";
import { fetchTtsUrl } from "../../utils/ttsBackend";

const sanitizeQuickReplyOptions = (options = []) => {
  if (!Array.isArray(options)) {
    return [];
  }
  const unique = [];
  for (const option of options) {
    const trimmed = typeof option === "string" ? option.trim() : "";
    if (!trimmed) {
      continue;
    }
    if (unique.some((entry) => entry.toLowerCase() === trimmed.toLowerCase())) {
      continue;
    }
    unique.push(trimmed);
    if (unique.length >= 4) {
      break;
    }
  }
  return unique;
};

const useLandscapeLayout = () => {
  const [isLandscape, setIsLandscape] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    return window.innerWidth >= window.innerHeight;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth >= window.innerHeight);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isLandscape;
};

const PersonalTrainerPage = () => {
  const theme = useTheme();
  const isLandscape = useLandscapeLayout();
  const [uid, setUid] = useState(auth.currentUser?.uid || null);
  const [messages, setMessages] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [incomingSuggestions, setIncomingSuggestions] = useState([]);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [loadingMessageId, setLoadingMessageId] = useState(null);
  const listRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (uid) {
      return undefined;
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return unsubscribe;
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setMessages([]);
      return undefined;
    }

    const messagesRef = collection(db, "users", uid, "trainerMessages");
    const messagesQuery = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const docs = snapshot.docs.map((doc) => {
        const data = doc.data();
        let timestamp = null;
        if (data.timestamp?.toDate) {
          timestamp = data.timestamp.toDate();
        } else if (data.timestamp instanceof Date) {
          timestamp = data.timestamp;
        } else if (typeof data.timestamp === "string") {
          const parsed = new Date(data.timestamp);
          timestamp = Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        return {
          id: doc.id,
          ...data,
          timestamp,
        };
      });
      setMessages(docs);
    });

    return unsubscribe;
  }, [uid]);

  useEffect(() => {
    if (!listRef.current) {
      return;
    }
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, pendingMessages]);

  const combinedMessages = useMemo(() => {
    if (!pendingMessages.length) {
      return messages;
    }
    return [...messages, ...pendingMessages];
  }, [messages, pendingMessages]);

  useEffect(() => {
    if (!incomingSuggestions.length) {
      return;
    }
    for (let index = combinedMessages.length - 1; index >= 0; index -= 1) {
      const message = combinedMessages[index];
      if (!message || message.role !== "assistant" || message.pending) {
        continue;
      }
      const options =
        Array.isArray(message.suggestedReplies) &&
        message.suggestedReplies.length
          ? message.suggestedReplies
          : message.metadata?.suggestedReplies;
      if (sanitizeQuickReplyOptions(options).length) {
        setIncomingSuggestions([]);
      }
      break;
    }
  }, [combinedMessages, incomingSuggestions, setIncomingSuggestions]);

  const quickReplies = useMemo(() => {
    if (incomingSuggestions.length) {
      return incomingSuggestions;
    }
    for (let index = combinedMessages.length - 1; index >= 0; index -= 1) {
      const message = combinedMessages[index];
      if (!message || message.role !== "assistant" || message.pending) {
        continue;
      }
      const options =
        Array.isArray(message.suggestedReplies) &&
        message.suggestedReplies.length
          ? message.suggestedReplies
          : message.metadata?.suggestedReplies;
      const sanitized = sanitizeQuickReplyOptions(options);
      if (sanitized.length) {
        return sanitized;
      }
    }
    return [];
  }, [combinedMessages, incomingSuggestions]);

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    []
  );

  const accentTone = useMemo(
    () =>
      theme.palette.secondary?.main ??
      theme.palette.primary.light ??
      theme.palette.primary.main,
    [
      theme.palette.primary.light,
      theme.palette.primary.main,
      theme.palette.secondary?.main,
    ]
  );

  const sendChatMessage = useCallback(
    async (messageText, { preserveDraft = false } = {}) => {
      const trimmed = messageText.trim();
      if (!trimmed || sending || !uid) {
        return false;
      }

      setError(null);
      setIncomingSuggestions([]);
      const pendingEntry = {
        id: `pending-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
        pending: true,
      };
      setPendingMessages((prev) => [...prev, pendingEntry]);
      if (!preserveDraft) {
        setNewMessage("");
      }
      setSending(true);

      try {
        const response = await sendMessageToPersonalTrainer(trimmed);
        const sanitized = sanitizeQuickReplyOptions(response?.suggestedReplies);
        setIncomingSuggestions(sanitized);
        setPendingMessages((prev) =>
          prev.filter((entry) => entry.id !== pendingEntry.id)
        );
        return true;
      } catch (err) {
        console.error("personalTrainerChat send error", err);
        setError(
          err?.message ||
            "Your trainer is unavailable right now. Please try again."
        );
        setPendingMessages((prev) =>
          prev.map((entry) =>
            entry.id === pendingEntry.id
              ? { ...entry, pending: false, failed: true }
              : entry
          )
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [sending, uid]
  );

  const handleQuickReply = useCallback(
    async (text) => {
      await sendChatMessage(text, { preserveDraft: true });
    },
    [sendChatMessage]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    await sendChatMessage(newMessage);
  };

  const handlePlayAudio = async (messageId, text) => {
    if (!text || !uid) {
      return;
    }

    if (audioRef.current && playingMessageId === messageId) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlayingMessageId(null);
      return;
    }

    setLoadingMessageId(messageId);
    try {
      const url = await fetchTtsUrl({
        text,
        voiceHint: "motivational_coach",
        cacheKey: `personal_trainer_${uid}_${messageId}`,
      });

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.addEventListener("ended", () => {
        setPlayingMessageId(null);
        setLoadingMessageId(null);
      });
      audio.play().catch((err) => {
        console.error("Failed to play trainer audio", err);
        setPlayingMessageId(null);
        setLoadingMessageId(null);
      });
      setPlayingMessageId(messageId);
    } catch (err) {
      console.error("personalTrainerChat TTS error", err);
    } finally {
      setLoadingMessageId(null);
    }
  };

  const handleStopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingMessageId(null);
  };

  const barBackground = useMemo(() => {
    const base =
      theme.palette.mode === "light"
        ? alpha(theme.palette.background.paper, 0.92)
        : alpha(theme.palette.background.default, 0.75);
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.2 : 0.45
    );
    const accentGlow = alpha(
      accentTone,
      theme.palette.mode === "light" ? 0.16 : 0.36
    );
    return `linear-gradient(120deg, ${primaryGlow} 0%, ${accentGlow} 55%, ${base} 100%)`;
  }, [accentTone, theme]);

  const chatOrder = isLandscape ? 0 : 1;
  const imageOrder = isLandscape ? 1 : 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isLandscape ? "row" : "column",
        height: "100%",
        gap: 3,
        p: { xs: 2, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          order: imageOrder,
          flex: isLandscape ? 1 : 0.9,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          background: barBackground,
          borderRadius: 4,
          boxShadow: theme.shadows[8],
          p: { xs: 2, md: 4 },
          minHeight: isLandscape ? "auto" : "35vh",
          gap: 3,
          maxHeight: "100%",
          overflowY: "auto",
        }}
      >
        <Box
          component="img"
          src="/assets/Muscles_front_and_back.svg"
          alt="Personal trainer anatomy illustration"
          sx={{
            width: "100%",
            maxWidth: 500,
            maxHeight: { xs: "40vh", md: "45vh" },
            objectFit: "contain",
            filter:
              theme.palette.mode === "dark"
                ? "drop-shadow(0 12px 28px rgba(0,0,0,0.5))"
                : "drop-shadow(0 18px 32px rgba(0,0,0,0.18))",
          }}
        />
        <Box
          component="img"
          src="/assets/human_organs.svg"
          alt="Human organs illustration"
          sx={{
            width: "100%",
            maxWidth: 500,
            maxHeight: { xs: "40vh", md: "45vh" },
            objectFit: "contain",
            filter:
              theme.palette.mode === "dark"
                ? "drop-shadow(0 12px 28px rgba(0,0,0,0.5))"
                : "drop-shadow(0 18px 32px rgba(0,0,0,0.18))",
          }}
        />
      </Box>
      <Box
        sx={{
          order: chatOrder,
          flex: 1.1,
          display: "flex",
          flexDirection: "column",
          minHeight: isLandscape ? "auto" : "45vh",
          backdropFilter: "blur(6px)",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            borderRadius: 4,
            overflow: "hidden",
            backgroundColor: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: "blur(12px)",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          }}
        >
          <Box
            sx={{
              p: { xs: 2, md: 3 },
              borderBottom: `1px solid ${alpha(
                theme.palette.primary.main,
                0.18
              )}`,
              background: barBackground,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Chat with Your Personal Trainer
            </Typography>
          </Box>
          <Box
            ref={listRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              p: { xs: 2, md: 3 },
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {combinedMessages.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography variant="body1" sx={{ opacity: 0.7 }}>
                  Share how you are feeling, what equipment you have, or what
                  goal you want to conquer next. Your trainer will personalize a
                  plan and keep your profile up to date.
                </Typography>
              </Box>
            ) : (
              combinedMessages.map((message) => {
                const isUser = message.role === "user";
                const pending = message.pending;
                const failed = message.failed;
                const highlighted = !isUser;
                return (
                  <Box
                    key={message.id}
                    sx={{
                      alignSelf: isUser ? "flex-end" : "flex-start",
                      maxWidth: "min(540px, 100%)",
                      width: "fit-content",
                      backgroundColor: isUser
                        ? alpha(theme.palette.primary.main, 0.14)
                        : alpha(accentTone, 0.1),
                      color: isUser
                        ? theme.palette.text.primary
                        : theme.palette.mode === "dark"
                        ? theme.palette.common.white
                        : theme.palette.text.primary,
                      borderRadius: 3,
                      p: 2.2,
                      boxShadow: highlighted
                        ? theme.shadows[6]
                        : theme.shadows[1],
                      border: `1px solid ${alpha(
                        highlighted ? accentTone : theme.palette.primary.main,
                        0.18
                      )}`,
                      position: "relative",
                      opacity: pending ? 0.6 : 1,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                        fontWeight: isUser ? 500 : 400,
                      }}
                    >
                      {message.content}
                    </Typography>
                    {!isUser && (
                      <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                        <Tooltip title="Play audio coaching">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handlePlayAudio(message.id, message.content)
                              }
                              disabled={loadingMessageId === message.id}
                              sx={{
                                color: accentTone,
                                backgroundColor: alpha(accentTone, 0.12),
                                "&:hover": {
                                  backgroundColor: alpha(accentTone, 0.2),
                                },
                              }}
                            >
                              {playingMessageId === message.id ? (
                                <StopRoundedIcon fontSize="small" />
                              ) : (
                                <VolumeUpRoundedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                    {failed && (
                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Message failed to send. Please try again.
                      </Alert>
                    )}
                  </Box>
                );
              })
            )}
          </Box>
          {quickReplies.length > 0 && (
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                pb: { xs: 1.5, md: 2 },
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {quickReplies.map((reply) => (
                <Button
                  key={reply}
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuickReply(reply)}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {reply}
                </Button>
              ))}
            </Box>
          )}
          <Divider sx={{ opacity: 0.6 }} />
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ p: { xs: 2, md: 3 } }}
          >
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Share your focus, questions, or wins"
              value={newMessage}
              onChange={(event) => setNewMessage(event.target.value)}
              disabled={sending}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {error && (
                  <Alert severity="error" sx={{ m: 0 }}>
                    {error}
                  </Alert>
                )}
              </Box>
              <Button
                type="submit"
                variant="contained"
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} />
                    Sending
                  </Box>
                ) : (
                  "Send"
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
        {playingMessageId && (
          <Box sx={{ mt: 2, alignSelf: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleStopAudio}
              startIcon={<StopRoundedIcon />}
            >
              Stop Audio
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PersonalTrainerPage;
